'use client'

import Image from 'next/image';
import React, { useState, useMemo } from 'react';
import { OnlineStatus } from './online-status';
import UserIcon from '@/components/ui/mixus-custom-icons-dir/user-icon'
import { IconAlert, IconCheck, IconInfo, IconShield, IconShieldCheck } from '@/components/ui/icons-v2'
import { optimizeClerkImageUrl } from '@/lib/utils/image';

type ImageSizePreset = 'small' | 'thumb' | 'medium' | 'big' | 'large';

interface SizeConfig {
  cssClass: string;
  dimensions: number;
}

const SIZE_PRESETS: Record<ImageSizePreset, SizeConfig> = {
  small: { cssClass: 'w-10 h-10', dimensions: 46 },
  thumb: { cssClass: 'w-12 h-12', dimensions: 96 },
  medium: { cssClass: 'w-28 h-28', dimensions: 256 },
  big: { cssClass: 'w-36 h-36', dimensions: 512 },
  large: { cssClass: 'w-48 h-48', dimensions: 1024 }
};

/**
 * Extracts the size value from a CSS class name
 * Handles both numeric classes (w-6) and text sizes (w-sm)
 */
const extractSizeFromCssClass = (cssClass: string): number => {
  // Try to find a numeric value in the class name (e.g., w-6, h-6)
  const numericMatch = cssClass.match(/[wh]-(\d+)/);
  if (numericMatch && numericMatch[1]) {
    const size = parseInt(numericMatch[1], 10);
    // Convert the Tailwind size to actual pixels
    // For small sizes, this is roughly 0.25rem per unit (4px)
    return size * 4;
  }
  
  // Handle common Tailwind text size classes
  if (cssClass.includes('w-xs') || cssClass.includes('h-xs')) return 20;
  if (cssClass.includes('w-sm') || cssClass.includes('h-sm')) return 24;
  if (cssClass.includes('w-md') || cssClass.includes('h-md')) return 32;
  if (cssClass.includes('w-lg') || cssClass.includes('h-lg')) return 40;
  if (cssClass.includes('w-xl') || cssClass.includes('h-xl')) return 48;
  
  // Default size if we can't determine
  return 96;
};

interface ProfilePicProps {
  imageUrl: string;
  altText: string;
  sizePreset?: ImageSizePreset;
  size?: string; // Keep for backward compatibility
  priority?: boolean;
  onClick?: () => void;
  className?: string;
  useLastActiveAt?: string;
  showOnlineStatus?: boolean;
  userId?: string;
  onlineStatusPosition?: 'bottom-right' | 'center';
  requestStatus?: boolean;
  pulse?: boolean;
  verified?: string | null;
  verifiedSize?: number;
  isAlert?: boolean;
  isInfo?: boolean;
  quality?: number;
}

const ProfilePic: React.FC<ProfilePicProps> = React.memo(({
  imageUrl,
  altText = 'profile picture',
  sizePreset = 'thumb',
  size, // Keep for backward compatibility
  priority = false,
  onClick,
  className,
  showOnlineStatus = true,
  userId,
  onlineStatusPosition = 'bottom-right',
  requestStatus = false,
  pulse = true,
  isAlert = false,
  isInfo = false,
  verified = null,
  verifiedSize = 3,
  quality = 85
}) => {
  const [isError, setIsError] = useState(false);

  // Use size preset or fallback to custom size
  const sizeConfig = useMemo(() => {
    if (size) {
      // For custom size, extract dimensions from the class name
      const widthSize = extractSizeFromCssClass(size.split(' ').find(c => c.startsWith('w-')) || '');
      const heightSize = extractSizeFromCssClass(size.split(' ').find(c => c.startsWith('h-')) || '');
      // Use the larger dimension, with a minimum of 24px for quality
      const dimensions = Math.max(widthSize, heightSize, 24);
      return { cssClass: size, dimensions };
    }
    return SIZE_PRESETS[sizePreset];
  }, [size, sizePreset]);

  const optimizedImageUrl = useMemo(() =>
    optimizeClerkImageUrl(imageUrl, sizeConfig.dimensions, quality),
    [imageUrl, sizeConfig.dimensions, quality]
  );

  const imageContent = useMemo(() => {
    if (optimizedImageUrl && !isError) {
      return (
        <Image
          src={optimizedImageUrl}
          alt={altText}
          width={sizeConfig.dimensions}
          height={sizeConfig.dimensions}
          quality={quality}
          style={{
            objectFit: 'cover',
            width: '100%',
            height: '100%',
            maxWidth: '100%'
          }}
          onError={(e) => {
            console.warn(`Image load failed for ${optimizedImageUrl}:`, e);
            setIsError(true);
          }}
          priority={priority}
        />
      );
    }
    else {
      if (isAlert){
        return (
          <div className="absolute inset-0 flex items-center justify-center">
            <IconAlert className="w-full text-mixus-coral-red" />
          </div>
        )
      }
      else if (isInfo) {
        return (
          <div className="absolute inset-0 flex items-center justify-center">
            <IconInfo className="w-full text-green-800
            " />
          </div>
        )
      }
      return (
        <div className="absolute inset-0 flex items-center justify-center">
          <UserIcon className="w-2/3 h-2/3" />
        </div>
      )
    }
  }, [optimizedImageUrl, isError, altText, sizePreset, priority, quality])

  return (
    <div className={`relative flex items-center ${className}`}>
      <div className="relative">
        <div
          className={`relative shrink-0 rounded-full overflow-hidden ${sizeConfig.cssClass} bg-muted/40 ${(!imageUrl && !isAlert && !isInfo) ? 'border-2 border-black dark:border-white' :'' }`}
          onClick={onClick}
        >
          {imageContent}
        </div>
        {verified && (
          <div className="absolute -bottom-0.5 -right-0.5 bg-white rounded-full p-0.5">
            <div className="relative">
              <IconShield
                className={`w-${verifiedSize} h-${verifiedSize} fill-mixus-royal-blue`}
                strokeWidth={0}
              />
              <IconCheck
                className={`w-${Math.ceil(verifiedSize / 2)} h-${Math.ceil(verifiedSize / 2)} text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2`}
              />
            </div>
          </div>
        )}

        {showOnlineStatus && userId && (
          <OnlineStatus
            userId={userId}
            pulse={pulse}
            position={onlineStatusPosition}
            onlineDotSize={verified ? verifiedSize - 1 : undefined}
            isVerified={verified || null}
          />
        )}
      </div>
    </div>
  )
});

ProfilePic.displayName = 'ProfilePic';

export default ProfilePic;
